import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Vehicle } from '@/types';
import { parseSlug } from '@/utils/vehicleSlug';
import { VehicleSEO } from '@/components/VehicleDetail/VehicleSEO';
import { VehicleBreadcrumb } from '@/components/VehicleDetail/Breadcrumb';
import { PhotoGallery } from '@/components/VehicleDetail/PhotoGallery';
import { QuickSummary } from '@/components/VehicleDetail/QuickSummary';
import { TechnicalSpecs } from '@/components/VehicleDetail/TechnicalSpecs';
import { ConditionHistory } from '@/components/VehicleDetail/ConditionHistory';
import { Trucks52Benefits } from '@/components/VehicleDetail/Trucks52Benefits';
import { CTABlock } from '@/components/VehicleDetail/CTABlock';
import { ViewCounter } from '@/components/VehicleDetail/ViewCounter';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { generateVehiclePDF } from '@/utils/generateVehiclePDF';
import { Header } from '@/components/Header';
import { ContactSection } from '@/components/ContactSection';

const VehicleDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Extraire l'ID du slug
  const vehicleIdPrefix = slug ? parseSlug(slug) : null;

  // Récupérer les données du véhicule
  const { data: vehicle, isLoading, error } = useQuery<Vehicle>({
    queryKey: ['vehicle', vehicleIdPrefix],
    queryFn: async () => {
      if (!vehicleIdPrefix) throw new Error('Invalid vehicle ID');

      // Rechercher par intervalle UUID basé sur le préfixe (8 hex)
      const prefix = vehicleIdPrefix.slice(0, 8).toLowerCase();
      const lower = `${prefix}-0000-0000-0000-000000000000`;
      const upper = `${prefix}-ffff-ffff-ffff-ffffffffffff`;

      console.debug('[VehicleDetail] Query bounds', { slug, vehicleIdPrefix, lower, upper });

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .gte('id', lower)
        .lte('id', upper)
        .eq('is_available', true)
        .maybeSingle();

      if (error || !data) {
        console.debug('[VehicleDetail] Query result', { error, hasData: !!data });
      }

      if (error) throw error;
      if (!data) throw new Error('Vehicle not found');

      // Transform database format to Vehicle type
      return {
        id: data.id,
        title: data.title,
        description: data.description || '',
        price: Number(data.price) || 0,
        images: data.images || [],
        videos: data.videos || [],
        category: data.category as Vehicle['category'],
        dateAdded: data.date_added,
        isAvailable: data.is_available,
        year: data.year || undefined,
        mileage: data.mileage || undefined,
        fuelType: data.fuel_type as Vehicle['fuelType'],
        transmission: data.transmission as Vehicle['transmission'],
        enginePower: data.engine_power || undefined,
        engineDisplacement: data.engine_displacement ? Number(data.engine_displacement) : undefined,
        axleConfiguration: data.axle_configuration || undefined,
        maxWeight: data.max_weight || undefined,
        payloadCapacity: data.payload_capacity || undefined,
        euroStandard: undefined, // Colonne non présente dans la DB actuellement
        critAir: undefined, // Colonne non présente dans la DB actuellement
        hasAirConditioning: data.has_air_conditioning || false,
        hasAbs: data.has_abs || false,
        hasEsp: data.has_esp || false,
        hasCruiseControl: data.has_cruise_control || false,
        hasNavigation: data.has_navigation || false,
        hasReversingCamera: data.has_reversing_camera || false,
        hasParkingSensors: data.has_parking_sensors || false,
        hasCentralLocking: data.has_central_locking || false,
        hasElectricWindows: data.has_electric_windows || false,
        hasHydraulicLift: data.has_hydraulic_lift || false,
        additionalEquipment: data.additional_equipment || [],
        frontTireSize: data.front_tire_size || undefined,
        rearTireSize: data.rear_tire_size || undefined,
        tireWearPercentage: undefined, // tire_condition est un texte, pas un pourcentage
        lastServiceDate: data.last_service_date || undefined,
        nextServiceDue: data.next_service_due || undefined,
        technicalInspectionDate: data.technical_inspection_date || undefined,
        technicalInspectionValidUntil: data.technical_inspection_valid_until || undefined,
        previousOwners: data.previous_owners || undefined,
        accidentHistory: data.accident_history || false,
        accidentDetails: data.accident_details || undefined,
        generalCondition: data.general_condition as Vehicle['generalCondition'],
        interiorCondition: data.interior_condition as Vehicle['interiorCondition'],
        exteriorCondition: data.exterior_condition as Vehicle['exteriorCondition'],
        usageType: data.usage_type as Vehicle['usageType'],
        parkingLocation: data.parking_location || undefined,
        availabilityDate: data.availability_date || undefined,
        warrantyRemaining: data.warranty_remaining || undefined,
        financingAvailable: data.financing_available || false,
        tradeInAccepted: data.trade_in_accepted || false,
        notes: data.notes || undefined,
        isVerified: data.is_verified || false,
        videoUrl: data.video_url || undefined,
        brand: undefined,
      } as Vehicle;
    },
    enabled: !!vehicleIdPrefix,
  });

  const handleDownloadPDF = async () => {
    if (!vehicle) return;
    
    setIsGeneratingPDF(true);
    try {
      await generateVehiclePDF(vehicle);
      toast({
        title: 'PDF généré',
        description: 'La fiche véhicule a été téléchargée avec succès.',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de générer le PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleRequestVisit = () => {
    toast({
      title: 'Demande envoyée',
      description: 'Nous vous contacterons rapidement pour organiser une visite.',
    });
    // TODO: Implémenter l'envoi d'email
  };

  const handleRequestVideo = () => {
    toast({
      title: 'Demande envoyée',
      description: 'Nous vous enverrons une vidéo du véhicule dans les plus brefs délais.',
    });
    // TODO: Implémenter l'edge function request-video
  };

  const handleRequestQuote = () => {
    const email = 'trucks.52@orange.fr';
    const subject = `Demande de devis - ${vehicle?.title}`;
    const body = `Bonjour,\n\nJe souhaite recevoir un devis pour le véhicule suivant :\n${vehicle?.title}\n\nCordialement`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleMakeOffer = () => {
    toast({
      title: 'Ouverture du client email',
      description: 'Vous pouvez maintenant envoyer votre offre par email.',
    });
    const email = 'trucks.52@orange.fr';
    const subject = `Offre d'achat - ${vehicle?.title}`;
    const body = `Bonjour,\n\nJe souhaite faire une offre pour le véhicule suivant :\n${vehicle?.title}\n\nMon offre : [Votre offre]\n\nCordialement`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleContact = () => {
    window.location.href = 'tel:+33608248529';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <h1 className="text-2xl font-bold mb-4">Véhicule non trouvé</h1>
        <p className="text-muted-foreground mb-6">
          Le véhicule que vous recherchez n'existe pas ou n'est plus disponible.
        </p>
        <Button asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* SEO meta tags */}
      <VehicleSEO vehicle={vehicle} />

      <div className="min-h-screen bg-background">
        <Header onAdminClick={() => navigate('/admin')} isAdmin={false} />

        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Bouton retour */}
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux annonces
          </Button>

          {/* Breadcrumb */}
          <VehicleBreadcrumb vehicle={vehicle} />

          {/* Compteur de vues */}
          <div className="mb-4">
            <ViewCounter vehicleId={vehicle.id} />
          </div>

          {/* Layout principal : galerie + résumé */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <PhotoGallery
                images={vehicle.images}
                videoUrl={vehicle.videoUrl}
                title={vehicle.title}
                euroStandard={vehicle.euroStandard}
                critAir={vehicle.critAir}
                isVerified={vehicle.isVerified}
              />
            </div>

            <div className="lg:col-span-1">
              <QuickSummary
                vehicle={vehicle}
                onRequestVisit={handleRequestVisit}
                onRequestVideo={handleRequestVideo}
                onDownloadPDF={handleDownloadPDF}
              />
            </div>
          </div>

          {/* Description */}
          {vehicle.description && (
            <div className="mb-8 p-6 bg-card rounded-lg border">
              <h2 className="text-2xl font-bold mb-4">Description</h2>
              <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                {vehicle.description}
              </p>
            </div>
          )}

          {/* Caractéristiques techniques */}
          <div className="mb-8">
            <TechnicalSpecs vehicle={vehicle} />
          </div>

          {/* État & Historique */}
          <div className="mb-8">
            <ConditionHistory vehicle={vehicle} />
          </div>

          {/* Atouts Trucks52 */}
          <div className="mb-8">
            <Trucks52Benefits />
          </div>

          {/* Bloc CTA final */}
          <CTABlock
            onRequestQuote={handleRequestQuote}
            onMakeOffer={handleMakeOffer}
            onContact={handleContact}
          />
        </main>

        <ContactSection />
      </div>
    </>
  );
};

export default VehicleDetailPage;
